import { useState, useEffect, useRef } from 'react';

/** Mirrors the platform's AzureStorageService.ALLOWED_MIME_TYPES and the 2 MB cap used in rebookt-web. */
const ACCEPTED_AVATAR_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
const MAX_AVATAR_BYTES = 2 * 1024 * 1024;
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuth } from '@/app/components/auth-context';
import { userApi, type UserProfile } from '@/api/user';
import { workHoursApi, type WorkHour } from '@/api/workHours';
import { getApiErrorMessage } from '@/api/errors';
import { TimeInput12h } from '@/app/components/time-input-12h';
import { ChevronLeft, ChevronRight, Lock, Eye, Bell, Info, LogOut, Loader2, Clock, Check, X } from 'lucide-react';

/* --- Toggle Switch --- */
function ToggleSwitch({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button type="button" role="switch" aria-checked={checked} onClick={() => onChange(!checked)}
      className="relative flex-shrink-0 transition-colors"
      style={{ width: '51px', height: '31px', borderRadius: '999px', backgroundColor: checked ? 'var(--terracotta)' : 'var(--muted-bg)', padding: 0, display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
      <div className="bg-white rounded-full shadow-sm" style={{ width: '27px', height: '27px', transform: checked ? 'translateX(22px)' : 'translateX(2px)', transition: 'transform 0.2s ease' }} />
    </button>
  );
}

/* --- Toggle row --- */
function ToggleRow({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-center justify-between" style={{ minHeight: '44px', padding: '10px 16px' }}>
      <span style={{ fontSize: '15px', color: 'var(--dark-teal)' }}>{label}</span>
      <ToggleSwitch checked={checked} onChange={onChange} />
    </div>
  );
}

/* --- Input field --- */
function SettingsInput({ label, type = 'text', value, placeholder, onChange, readOnly = false }: {
  label: string; type?: string; value: string; placeholder?: string; onChange: (v: string) => void; readOnly?: boolean;
}) {
  return (
    <div style={{ padding: '8px 16px' }}>
      <label className="block" style={{ fontSize: '12px', color: 'var(--muted-fg)', marginBottom: '6px' }}>{label}</label>
      <input type={type} value={value} placeholder={placeholder} onChange={e => onChange(e.target.value)} readOnly={readOnly}
        className="w-full border bg-white rounded-lg"
        style={{
          padding: '12px', borderColor: 'var(--border-color)', fontSize: '15px', color: 'var(--dark-teal)', height: '44px',
          backgroundColor: readOnly ? 'var(--cream)' : 'white',
          cursor: readOnly ? 'default' : 'text',
          opacity: readOnly ? 0.7 : 1,
        }} />
    </div>
  );
}

/* --- Section card --- */
function SectionCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--off-white)', border: '1px solid var(--border-color)', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
      {children}
    </div>
  );
}

function Divider() {
  return <div style={{ height: '1px', backgroundColor: 'var(--border-color)', marginLeft: '16px' }} />;
}

type SettingsSection = 'menu' | 'profile' | 'schedule' | 'password' | 'display' | 'notifications' | 'about';

const DAY_LABELS: Record<string, string> = {
  MONDAY: 'Lunes', TUESDAY: 'Martes', WEDNESDAY: 'Miércoles',
  THURSDAY: 'Jueves', FRIDAY: 'Viernes', SATURDAY: 'Sábado', SUNDAY: 'Domingo',
};

export function Settings() {
  const { signOut } = useAuth();
  const [activeSection, setActiveSection] = useState<SettingsSection>('menu');

  // User profile from API
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileSaving, setProfileSaving] = useState(false);
  const settingsQueryClient = useQueryClient();
  const [avatarSaving, setAvatarSaving] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const [profileForm, setProfileForm] = useState({ firstName: '', lastName: '', phone: '', bio: '', acceptingAppointments: false });

  // Password state
  const [passwordInfo, setPasswordInfo] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Display preferences (local only)
  const [showCompleted, setShowCompleted] = useState(true);
  const [showNotes, setShowNotes] = useState(true);
  const [showPhotos, setShowPhotos] = useState(true);

  // Notification preferences (local only)
  const [upcomingReminders, setUpcomingReminders] = useState(true);
  const [newBookingAlerts, setNewBookingAlerts] = useState(true);
  const [scheduleChange, setScheduleChange] = useState(true);

  useEffect(() => {
    const loadProfile = async () => {
      setProfileLoading(true);
      setProfileError(null);
      try {
        const data = await userApi.getMe();
        setProfile(data);
        setProfileForm({
          firstName: data.firstName ?? '',
          lastName: data.lastName ?? '',
          phone: data.phone ?? '',
          bio: data.bio ?? '',
          acceptingAppointments: data.acceptingAppointments ?? false,
        });
      } catch (err) {
        console.error('Error al cargar perfil:', err);
        setProfileError('No se pudo cargar el perfil.');
      } finally {
        setProfileLoading(false);
      }
    };
    loadProfile();
  }, []);

  const getInitials = () => {
    if (!profile) return '--';
    return ((profile.firstName?.[0] ?? '') + (profile.lastName?.[0] ?? '')).toUpperCase() || '--';
  };

  const handlePickAvatar = () => avatarInputRef.current?.click();

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!ACCEPTED_AVATAR_TYPES.includes(file.type?.toLowerCase())) {
      toast.error('Usa una imagen PNG, JPG o WEBP');
      return;
    }
    if (file.size > MAX_AVATAR_BYTES) {
      toast.error('La imagen debe pesar 2 MB o menos');
      return;
    }
    setAvatarSaving(true);
    try {
      const { avatarUrl } = await userApi.uploadAvatar(file);
      setProfile(p => (p ? { ...p, avatarUrl } : p));
      settingsQueryClient.invalidateQueries({ queryKey: ['me'] });
      toast.success('Foto actualizada');
    } catch (err) {
      console.error('Error al subir la foto:', err);
      toast.error('No se pudo subir la foto');
    } finally {
      setAvatarSaving(false);
    }
  };

  const handleRemoveAvatar = async () => {
    setAvatarSaving(true);
    try {
      await userApi.deleteAvatar();
      setProfile(p => (p ? { ...p, avatarUrl: null } : p));
      settingsQueryClient.invalidateQueries({ queryKey: ['me'] });
      toast.success('Foto eliminada');
    } catch (err) {
      console.error('Error al eliminar la foto:', err);
      toast.error('No se pudo eliminar la foto');
    } finally {
      setAvatarSaving(false);
    }
  };

  const profileDirty = profile != null && (
    profileForm.firstName !== (profile.firstName ?? '') ||
    profileForm.lastName !== (profile.lastName ?? '') ||
    profileForm.phone !== (profile.phone ?? '') ||
    profileForm.bio !== (profile.bio ?? '') ||
    profileForm.acceptingAppointments !== (profile.acceptingAppointments ?? false)
  );

  const profileValid = profileForm.firstName.trim().length > 0 && profileForm.lastName.trim().length > 0;

  const handleSaveProfile = async () => {
    if (!profileDirty || !profileValid) {
      if (!profileValid) toast.error('Nombre y apellido son obligatorios');
      return;
    }
    setProfileSaving(true);
    try {
      const updated = await userApi.updateProfile({
        firstName: profileForm.firstName || undefined,
        lastName: profileForm.lastName || undefined,
        phone: profileForm.phone || undefined,
        bio: profileForm.bio || undefined,
        acceptingAppointments: profileForm.acceptingAppointments,
      });
      setProfile(updated);
      toast.success('Perfil actualizado');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'No se pudo actualizar el perfil'));
    } finally {
      setProfileSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    setPasswordMessage(null);

    if (!passwordInfo.currentPassword || !passwordInfo.newPassword) {
      setPasswordMessage({ type: 'error', text: 'Todos los campos son obligatorios.' });
      return;
    }
    if (passwordInfo.newPassword !== passwordInfo.confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'Las contrasenas no coinciden.' });
      return;
    }
    const pw = passwordInfo.newPassword;
    if (pw.length < 8 || !/[A-Z]/.test(pw) || !/\d/.test(pw) || !/[!@#$&*]/.test(pw)) {
      setPasswordMessage({
        type: 'error',
        text: 'La nueva contraseña debe tener al menos 8 caracteres, una mayúscula, un número y un carácter especial (!@#$&*).',
      });
      return;
    }

    setPasswordSaving(true);
    try {
      await userApi.changePassword(passwordInfo.currentPassword, passwordInfo.newPassword);
      setPasswordMessage({ type: 'success', text: 'Contraseña actualizada correctamente.' });
      setPasswordInfo({ currentPassword: '', newPassword: '', confirmPassword: '' });
      toast.success('Contraseña actualizada');
    } catch (err) {
      const msg = getApiErrorMessage(err, 'No se pudo actualizar la contraseña.');
      setPasswordMessage({ type: 'error', text: msg });
      toast.error(msg);
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleSignOut = () => {
    signOut();
  };

  /* --- Detail header with back button --- */
  const DetailHeader = ({ title }: { title: string }) => (
    <div className="flex items-center gap-2 mb-4">
      <button onClick={() => setActiveSection('menu')} className="flex items-center gap-1 rounded-lg transition-colors"
        style={{ padding: '6px 10px', fontSize: '14px', color: 'var(--terracotta)', cursor: 'pointer', marginLeft: '-10px' }}>
        <ChevronLeft size={18} strokeWidth={2} />
        <span>Ajustes</span>
      </button>
      <div style={{ fontFamily: 'var(--font-heading)', fontSize: '18px', color: 'var(--dark-teal)', marginLeft: 'auto', marginRight: 'auto', paddingRight: '80px' }}>
        {title}
      </div>
    </div>
  );

  /* --- Menu item row --- */
  const MenuItem = ({ icon: Icon, label, subtitle, onClick, destructive }: {
    icon: React.ElementType; label: string; subtitle?: string; onClick: () => void; destructive?: boolean;
  }) => (
    <button onClick={onClick} className="w-full flex items-center gap-3 transition-colors hover:bg-[#FAF9F7]"
      style={{ padding: '12px 16px', cursor: 'pointer', minHeight: '52px', textAlign: 'left' }}>
      <div className="flex items-center justify-center rounded-lg flex-shrink-0"
        style={{ width: '32px', height: '32px', backgroundColor: destructive ? 'rgba(220,38,38,0.1)' : 'rgba(224,123,84,0.1)', borderRadius: '8px' }}>
        <Icon size={17} style={{ color: destructive ? 'var(--destructive)' : 'var(--terracotta)' }} />
      </div>
      <div className="flex-1 min-w-0">
        <div style={{ fontSize: '15px', color: destructive ? 'var(--destructive)' : 'var(--dark-teal)' }}>{label}</div>
        {subtitle && <div className="truncate" style={{ fontSize: '12px', color: 'var(--muted-fg)', marginTop: '1px' }}>{subtitle}</div>}
      </div>
      {!destructive && <ChevronRight size={16} style={{ color: 'var(--border-color)', flexShrink: 0 }} />}
    </button>
  );

  /* ========= MAIN MENU ========= */
  if (activeSection === 'menu') {
    return (
      <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--cream)' }}>
        <div className="flex-1 overflow-auto pb-24">
          <div style={{ padding: '16px', maxWidth: '600px', margin: '0 auto' }}>

            {/* Profile card */}
            <SectionCard>
              <button onClick={() => setActiveSection('profile')}
                className="w-full flex items-center gap-3.5 transition-colors hover:bg-[#FAF9F7]"
                style={{ padding: '16px', cursor: 'pointer', textAlign: 'left' }}>
                <div className="rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ width: '56px', height: '56px', backgroundColor: 'var(--terracotta)' }}>
                  {profileLoading ? (
                    <Loader2 size={20} className="animate-spin text-white" />
                  ) : (
                    <span className="text-white" style={{ fontFamily: 'var(--font-heading)', fontSize: '20px' }}>{getInitials()}</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  {profileLoading ? (
                    <div style={{ fontSize: '13px', color: 'var(--muted-fg)' }}>Cargando...</div>
                  ) : profile ? (
                    <>
                      <div className="truncate" style={{ fontFamily: 'var(--font-heading)', fontSize: '18px', color: 'var(--dark-teal)' }}>
                        {profile.firstName} {profile.lastName}
                      </div>
                      <div className="truncate" style={{ fontSize: '13px', color: 'var(--muted-fg)', marginTop: '2px' }}>
                        {profile.email}
                      </div>
                    </>
                  ) : (
                    <div style={{ fontSize: '13px', color: 'var(--muted-fg)' }}>{profileError || 'Error al cargar'}</div>
                  )}
                </div>
                <ChevronRight size={18} style={{ color: 'var(--border-color)', flexShrink: 0 }} />
              </button>
            </SectionCard>

            {/* Account */}
            <div style={{ marginTop: '24px', marginBottom: '8px', paddingLeft: '16px' }}>
              <span className="section-label">CUENTA</span>
            </div>
            <SectionCard>
              <MenuItem icon={Clock} label="Mi horario" subtitle="Días y horas de trabajo" onClick={() => setActiveSection('schedule')} />
              <Divider />
              <MenuItem icon={Lock} label="Contraseña y Seguridad" subtitle="Cambiar tu contraseña" onClick={() => setActiveSection('password')} />
            </SectionCard>

            {/* Preferences */}
            <div style={{ marginTop: '24px', marginBottom: '8px', paddingLeft: '16px' }}>
              <span className="section-label">PREFERENCIAS</span>
            </div>
            <SectionCard>
              <MenuItem icon={Eye} label="Pantalla" subtitle="Completadas, notas, fotos" onClick={() => setActiveSection('display')} />
              <Divider />
              <MenuItem icon={Bell} label="Notificaciones" subtitle="Recordatorios, alertas" onClick={() => setActiveSection('notifications')} />
            </SectionCard>

            {/* Other */}
            <div style={{ marginTop: '24px', marginBottom: '8px', paddingLeft: '16px' }}>
              <span className="section-label">OTROS</span>
            </div>
            <SectionCard>
              <MenuItem icon={Info} label="Acerca de RebookT" subtitle="Version 1.0.0" onClick={() => setActiveSection('about')} />
              <Divider />
              <MenuItem icon={LogOut} label="Cerrar sesión" onClick={handleSignOut} destructive />
            </SectionCard>

            <div style={{ height: '32px' }} />
          </div>
        </div>
      </div>
    );
  }

  /* ========= PROFILE ========= */
  if (activeSection === 'profile') {
    return (
      <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--cream)' }}>
        <div className="flex-1 overflow-auto pb-24">
          <div style={{ padding: '16px', maxWidth: '600px', margin: '0 auto' }}>
            <DetailHeader title="Perfil" />

            {profileLoading ? (
              <div className="flex items-center justify-center" style={{ padding: '60px 0' }}>
                <div className="flex items-center gap-3" style={{ color: 'var(--muted-fg)', fontSize: '14px' }}>
                  <Loader2 size={20} className="animate-spin" />
                  <span>Cargando perfil...</span>
                </div>
              </div>
            ) : profile ? (
              <>
                {/* Avatar */}
                <div className="flex flex-col items-center mb-5">
                  <button
                    type="button"
                    onClick={handlePickAvatar}
                    disabled={avatarSaving}
                    aria-label="Cambiar foto de perfil"
                    className="rounded-full flex items-center justify-center overflow-hidden disabled:opacity-60"
                    style={{ width: '80px', height: '80px', backgroundColor: 'var(--terracotta)', marginBottom: '10px' }}>
                    {profile.avatarUrl ? (
                      <img
                        src={profile.avatarUrl}
                        alt="Tu foto de perfil"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-white" style={{ fontFamily: 'var(--font-heading)', fontSize: '28px' }}>{getInitials()}</span>
                    )}
                  </button>
                  <input
                    ref={avatarInputRef}
                    type="file"
                    accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/jpg,image/webp"
                    className="hidden"
                    onChange={handleAvatarChange}
                    disabled={avatarSaving}
                  />
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={handlePickAvatar}
                      disabled={avatarSaving}
                      className="disabled:opacity-60"
                      style={{ color: 'var(--terracotta)', fontSize: 'var(--text-sm)' }}>
                      {avatarSaving ? 'Guardando...' : profile.avatarUrl ? 'Cambiar foto' : 'Subir foto'}
                    </button>
                    {profile.avatarUrl && !avatarSaving && (
                      <button
                        type="button"
                        onClick={handleRemoveAvatar}
                        style={{ color: 'var(--text-muted, #6b7280)', fontSize: 'var(--text-sm)' }}>
                        Quitar
                      </button>
                    )}
                  </div>
                </div>

                {/* Editable fields */}
                <SectionCard>
                  <SettingsInput label="Nombre" value={profileForm.firstName} onChange={v => setProfileForm(p => ({ ...p, firstName: v }))} />
                  <Divider />
                  <SettingsInput label="Apellido" value={profileForm.lastName} onChange={v => setProfileForm(p => ({ ...p, lastName: v }))} />
                  <Divider />
                  <SettingsInput label="Email" type="email" value={profile.email} onChange={() => {}} readOnly />
                  <Divider />
                  <SettingsInput label="Teléfono" type="tel" value={profileForm.phone} placeholder="Sin teléfono" onChange={v => setProfileForm(p => ({ ...p, phone: v }))} />
                  <div style={{ height: '8px' }} />
                </SectionCard>

                {/* Bio */}
                <div style={{ marginTop: '16px' }}>
                  <SectionCard>
                    <div style={{ padding: '8px 16px' }}>
                      <label className="block" style={{ fontSize: '12px', color: 'var(--muted-fg)', marginBottom: '6px' }}>Biografía</label>
                      <textarea
                        value={profileForm.bio}
                        onChange={e => setProfileForm(p => ({ ...p, bio: e.target.value }))}
                        placeholder="Cuéntale a tus clientes sobre ti..."
                        rows={3}
                        className="w-full border bg-white rounded-lg resize-none"
                        style={{ padding: '12px', borderColor: 'var(--border-color)', fontSize: '15px', color: 'var(--dark-teal)', lineHeight: 1.5 }}
                      />
                    </div>
                    <div style={{ height: '8px' }} />
                  </SectionCard>
                </div>

                {/* Accepting appointments */}
                <div style={{ marginTop: '16px' }}>
                  <SectionCard>
                    <ToggleRow label="Aceptando citas" checked={profileForm.acceptingAppointments} onChange={v => setProfileForm(p => ({ ...p, acceptingAppointments: v }))} />
                  </SectionCard>
                  <p style={{ fontSize: '12px', color: 'var(--muted-fg)', padding: '6px 16px', lineHeight: 1.4 }}>
                    Cuando está desactivado, los clientes no podrán reservar citas contigo.
                  </p>
                </div>

                {/* Save */}
                <div style={{ padding: '16px 0' }}>
                  <button
                    onClick={handleSaveProfile}
                    disabled={!profileDirty || !profileValid || profileSaving}
                    className="w-full rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                    style={{
                      height: '48px',
                      backgroundColor: profileDirty && profileValid ? 'var(--terracotta)' : 'var(--muted-bg)',
                      color: profileDirty && profileValid ? 'white' : 'var(--muted-fg)',
                      fontSize: '15px', fontWeight: 500, borderRadius: 'var(--radius-md)',
                      cursor: profileDirty && profileValid ? 'pointer' : 'default', border: 'none',
                    }}>
                    {profileSaving && <Loader2 size={16} className="animate-spin" />}
                    {profileSaving ? 'Guardando...' : 'Guardar cambios'}
                  </button>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center" style={{ padding: '60px 0' }}>
                <div style={{ fontSize: '14px', color: 'var(--muted-fg)' }}>{profileError || 'Error al cargar el perfil'}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  /* ========= SCHEDULE ========= */
  if (activeSection === 'schedule') {
    return <ScheduleSection onBack={() => setActiveSection('menu')} />;
  }

  /* ========= PASSWORD ========= */
  if (activeSection === 'password') {
    return (
      <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--cream)' }}>
        <div className="flex-1 overflow-auto pb-24">
          <div style={{ padding: '16px', maxWidth: '600px', margin: '0 auto' }}>
            <DetailHeader title="Contraseña y Seguridad" />
            <SectionCard>
              <SettingsInput label="Contraseña actual" type="password" value={passwordInfo.currentPassword} placeholder="Ingresa tu contraseña actual" onChange={v => setPasswordInfo({ ...passwordInfo, currentPassword: v })} />
              <Divider />
              <SettingsInput label="Nueva contraseña" type="password" value={passwordInfo.newPassword} placeholder="Ingresa la nueva contraseña" onChange={v => setPasswordInfo({ ...passwordInfo, newPassword: v })} />
              {passwordInfo.newPassword && (
                <div style={{ padding: '4px 16px 8px' }}>
                  {[
                    { label: 'Mínimo 8 caracteres', met: passwordInfo.newPassword.length >= 8 },
                    { label: 'Al menos una mayúscula', met: /[A-Z]/.test(passwordInfo.newPassword) },
                    { label: 'Al menos un número', met: /\d/.test(passwordInfo.newPassword) },
                    { label: 'Al menos un carácter especial (!@#$&*)', met: /[!@#$&*]/.test(passwordInfo.newPassword) },
                  ].map(c => (
                    <div key={c.label} className="flex items-center gap-2" style={{ fontSize: '12px', marginBottom: '2px' }}>
                      {c.met ? <Check size={14} style={{ color: '#252E3E' }} /> : <X size={14} style={{ color: 'var(--muted-fg)', opacity: 0.4 }} />}
                      <span style={{ color: c.met ? '#252E3E' : 'var(--muted-fg)' }}>{c.label}</span>
                    </div>
                  ))}
                </div>
              )}
              <Divider />
              <SettingsInput label="Confirmar nueva contraseña" type="password" value={passwordInfo.confirmPassword} placeholder="Confirma la nueva contraseña" onChange={v => setPasswordInfo({ ...passwordInfo, confirmPassword: v })} />
              {passwordInfo.confirmPassword && (
                <div style={{ padding: '4px 16px 8px' }}>
                  <div className="flex items-center gap-2" style={{ fontSize: '12px' }}>
                    {passwordInfo.newPassword === passwordInfo.confirmPassword
                      ? <><Check size={14} style={{ color: '#252E3E' }} /><span style={{ color: '#252E3E' }}>Las contraseñas coinciden</span></>
                      : <><X size={14} style={{ color: '#dc2626' }} /><span style={{ color: '#dc2626' }}>Las contraseñas no coinciden</span></>
                    }
                  </div>
                </div>
              )}
              <div style={{ height: '8px' }} />
            </SectionCard>

            {passwordMessage && (
              <div className="rounded-lg mt-3" style={{
                padding: '10px 14px',
                fontSize: '13px',
                backgroundColor: passwordMessage.type === 'success' ? 'rgba(37, 46, 62, 0.08)' : 'rgba(220, 38, 38, 0.08)',
                color: passwordMessage.type === 'success' ? '#252E3E' : '#dc2626',
                border: `1px solid ${passwordMessage.type === 'success' ? 'rgba(37, 46, 62, 0.2)' : 'rgba(220, 38, 38, 0.2)'}`,
              }}>
                {passwordMessage.text}
              </div>
            )}

            {(() => {
              const pw = passwordInfo.newPassword;
              const allMet = pw.length >= 8 && /[A-Z]/.test(pw) && /\d/.test(pw) && /[!@#$&*]/.test(pw);
              const match = pw === passwordInfo.confirmPassword && pw.length > 0;
              const canSave = !!passwordInfo.currentPassword && allMet && match && !passwordSaving;
              return (
                <div style={{ padding: '16px 0' }}>
                  <button
                    onClick={handlePasswordChange}
                    disabled={!canSave}
                    className="w-full rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                    style={{
                      height: '48px',
                      backgroundColor: canSave ? 'var(--terracotta)' : 'var(--muted-bg)',
                      color: canSave ? 'white' : 'var(--muted-fg)',
                      fontSize: '15px', fontWeight: 500, borderRadius: 'var(--radius-md)',
                      cursor: canSave ? 'pointer' : 'default', border: 'none',
                    }}>
                    {passwordSaving && <Loader2 size={16} className="animate-spin" />}
                    {passwordSaving ? 'Actualizando...' : 'Actualizar contraseña'}
                  </button>
                </div>
              );
            })()}
          </div>
        </div>
      </div>
    );
  }

  /* ========= DISPLAY ========= */
  if (activeSection === 'display') {
    return (
      <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--cream)' }}>
        <div className="flex-1 overflow-auto pb-24">
          <div style={{ padding: '16px', maxWidth: '600px', margin: '0 auto' }}>
            <DetailHeader title="Pantalla" />
            <SectionCard>
              <ToggleRow label="Mostrar citas completadas" checked={showCompleted} onChange={setShowCompleted} />
              <Divider />
              <ToggleRow label="Mostrar notas de cita" checked={showNotes} onChange={setShowNotes} />
              <Divider />
              <ToggleRow label="Mostrar fotos de clientes" checked={showPhotos} onChange={setShowPhotos} />
            </SectionCard>
          </div>
        </div>
      </div>
    );
  }

  /* ========= NOTIFICATIONS ========= */
  if (activeSection === 'notifications') {
    return (
      <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--cream)' }}>
        <div className="flex-1 overflow-auto pb-24">
          <div style={{ padding: '16px', maxWidth: '600px', margin: '0 auto' }}>
            <DetailHeader title="Notificaciones" />
            <SectionCard>
              <ToggleRow label="Recordatorios de próximas citas" checked={upcomingReminders} onChange={setUpcomingReminders} />
              <Divider />
              <ToggleRow label="Alertas de nuevas reservas" checked={newBookingAlerts} onChange={setNewBookingAlerts} />
              <Divider />
              <ToggleRow label="Cambios de horario" checked={scheduleChange} onChange={setScheduleChange} />
            </SectionCard>
          </div>
        </div>
      </div>
    );
  }

  /* ========= ABOUT ========= */
  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--cream)' }}>
      <div className="flex-1 overflow-auto pb-24">
        <div style={{ padding: '16px', maxWidth: '600px', margin: '0 auto' }}>
          <DetailHeader title="Acerca de" />

          <div className="flex flex-col items-center mb-6">
            <div className="flex items-center justify-center rounded-2xl"
              style={{ width: '72px', height: '72px', backgroundColor: 'var(--dark-teal)', marginBottom: '12px' }}>
              <span className="text-white" style={{ fontFamily: 'var(--font-heading)', fontSize: '22px' }}>R</span>
            </div>
            <div style={{ fontFamily: 'var(--font-heading)', fontSize: '20px', color: 'var(--dark-teal)' }}>RebookT</div>
            <div style={{ fontSize: '13px', color: 'var(--muted-fg)', marginTop: '4px' }}>Gestor de Citas para Staff</div>
          </div>

          <SectionCard>
            <div className="flex items-center justify-between" style={{ padding: '12px 16px', minHeight: '44px' }}>
              <span style={{ fontSize: '15px', color: 'var(--dark-teal)' }}>Version</span>
              <span style={{ fontSize: '15px', color: 'var(--muted-fg)' }}>1.0.0</span>
            </div>
            <Divider />
            <div className="flex items-center justify-between" style={{ padding: '12px 16px', minHeight: '44px' }}>
              <span style={{ fontSize: '15px', color: 'var(--dark-teal)' }}>Build</span>
              <span style={{ fontSize: '15px', color: 'var(--muted-fg)' }}>2026.04.16</span>
            </div>
            <Divider />
            <div className="flex items-center justify-between" style={{ padding: '12px 16px', minHeight: '44px' }}>
              <span style={{ fontSize: '15px', color: 'var(--dark-teal)' }}>Plataforma</span>
              <span style={{ fontSize: '15px', color: 'var(--muted-fg)' }}>iPad / Tablet</span>
            </div>
          </SectionCard>

          <div style={{ textAlign: 'center', marginTop: '24px', fontSize: '12px', color: 'var(--muted-fg)' }}>
            Hecho con dedicacion para profesionales de salon.
          </div>
        </div>
      </div>
    </div>
  );
}

/* ========= SCHEDULE SECTION (separate component for cleanliness) ========= */

function ScheduleSection({ onBack }: { onBack: () => void }) {
  const queryClient = useQueryClient();

  const { data: workHours = [], isLoading } = useQuery({
    queryKey: ['my-work-hours'],
    queryFn: workHoursApi.getMyWorkHours,
    staleTime: 60_000,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, update }: { id: number; update: { enabled: boolean; startTime?: string | null; endTime?: string | null } }) =>
      workHoursApi.updateMyWorkHour(id, update),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-work-hours'] });
      toast.success('Horario actualizado');
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'No se pudo actualizar')),
  });

  // Local drafts of the time fields, debounced before sending the PUT so
  // rapid picker changes don't fire a request per keystroke.
  const [drafts, setDrafts] = useState<Record<number, { startTime: string; endTime: string }>>({});
  const timers = useRef<Record<number, ReturnType<typeof setTimeout>>>({});

  // Clear pending debounce timers on unmount so we don't fire after leaving.
  useEffect(() => {
    const pending = timers.current;
    return () => Object.values(pending).forEach(clearTimeout);
  }, []);

  const getDraft = (wh: WorkHour) =>
    drafts[wh.id] ?? {
      startTime: wh.startTime?.slice(0, 5) ?? '09:00',
      endTime: wh.endTime?.slice(0, 5) ?? '18:00',
    };

  const handleToggle = (wh: WorkHour) => {
    updateMutation.mutate({
      id: wh.id,
      update: {
        enabled: !wh.enabled,
        startTime: wh.startTime,
        endTime: wh.endTime,
      },
    });
  };

  const handleTimeChange = (wh: WorkHour, field: 'startTime' | 'endTime', value: string) => {
    const nextDraft = { ...getDraft(wh), [field]: value };
    setDrafts(prev => ({ ...prev, [wh.id]: nextDraft }));

    // Debounce the network write; validate before sending.
    if (timers.current[wh.id]) clearTimeout(timers.current[wh.id]);
    timers.current[wh.id] = setTimeout(() => {
      const { startTime, endTime } = nextDraft;
      if (!startTime || !endTime) return; // ignore a cleared field
      // "HH:MM" strings compare lexicographically in chronological order.
      if (startTime >= endTime) {
        toast.error('La hora de inicio debe ser antes de la hora de fin');
        return;
      }
      updateMutation.mutate({ id: wh.id, update: { enabled: wh.enabled, startTime, endTime } });
    }, 600);
  };

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--cream)' }}>
      <div className="flex-1 overflow-auto pb-24">
        <div style={{ padding: '16px', maxWidth: '600px', margin: '0 auto' }}>
          {/* Header */}
          <div className="flex items-center gap-2 mb-4">
            <button onClick={onBack} className="flex items-center gap-1 rounded-lg transition-colors"
              style={{ padding: '6px 10px', fontSize: '14px', color: 'var(--terracotta)', cursor: 'pointer', marginLeft: '-10px', background: 'none', border: 'none' }}>
              <ChevronLeft size={18} strokeWidth={2} />
              <span>Ajustes</span>
            </button>
            <div style={{ fontFamily: 'var(--font-heading)', fontSize: '18px', color: 'var(--dark-teal)', marginLeft: 'auto', marginRight: 'auto', paddingRight: '80px' }}>
              Mi horario
            </div>
          </div>

          <p style={{ fontSize: '13px', color: 'var(--muted-fg)', marginBottom: '16px', lineHeight: 1.5 }}>
            Define tus días y horas de trabajo. Los clientes solo podrán reservar en los horarios habilitados.
          </p>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={24} className="animate-spin" style={{ color: 'var(--muted-fg)' }} />
            </div>
          ) : workHours.length === 0 ? (
            <div className="rounded-xl text-center py-8" style={{ backgroundColor: 'var(--off-white)', border: '1px solid var(--border-color)', fontSize: '14px', color: 'var(--muted-fg)' }}>
              No hay horario configurado. Pídele a tu administrador que configure tu horario inicial.
            </div>
          ) : (
            <div className="space-y-3">
              {workHours.map(wh => (
                <div
                  key={wh.id}
                  className="rounded-xl transition-colors"
                  style={{
                    backgroundColor: 'var(--off-white)',
                    border: `1px solid ${wh.enabled ? 'var(--border-color)' : 'transparent'}`,
                    padding: '14px 16px',
                    opacity: wh.enabled ? 1 : 0.6,
                  }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span style={{ fontSize: '15px', fontWeight: 600, color: 'var(--dark-teal)' }}>
                      {DAY_LABELS[wh.dayOfWeek] ?? wh.dayOfWeek}
                    </span>
                    <ToggleSwitch
                      checked={wh.enabled}
                      onChange={() => handleToggle(wh)}
                    />
                  </div>
                  {wh.enabled && (
                    <div className="flex items-center gap-3">
                      <div>
                        <label style={{ fontSize: '11px', color: 'var(--muted-fg)', display: 'block', marginBottom: '4px' }}>Inicio</label>
                        <TimeInput12h
                          value={getDraft(wh).startTime}
                          onChange={value => handleTimeChange(wh, 'startTime', value)}
                        />
                      </div>
                      <span style={{ fontSize: '14px', color: 'var(--muted-fg)', paddingTop: '18px' }}>—</span>
                      <div>
                        <label style={{ fontSize: '11px', color: 'var(--muted-fg)', display: 'block', marginBottom: '4px' }}>Fin</label>
                        <TimeInput12h
                          value={getDraft(wh).endTime}
                          onChange={value => handleTimeChange(wh, 'endTime', value)}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
